if (!customElements.get('piecework-media-gallery')) {
  customElements.define(
    'piecework-media-gallery',
    class PieceworkMediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          slideshow: this.querySelector('.splide'),
          thumbnails: this.querySelectorAll('.pw-media-thumbnails button'),
          pagination: this.querySelectorAll('.pw-media-pagination div'),
          slides: this.querySelectorAll('.splide li'),
        };
        // Respect reduced-motion: videos never autoplay, user can still press play.
        this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Per-video "user paused this" flag so slide changes don't restart a video someone stopped.
        this.userPaused = new WeakMap();
      }

      setActiveMedia(media, bool) {
        this.elements.slides.forEach((slide) => {
          const target = slide.dataset.target;
          if (media === target) {
            const slideNum = parseInt(slide.dataset.slide);
            this.slider.go(slideNum);
          }
        });
      }

      // --- video helpers -------------------------------------------------------

      playVideo(video) {
        if (!video || this.reducedMotion || this.userPaused.get(video)) return;
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
        this.syncToggle(video, true);
      }

      pauseVideo(video, rewind) {
        if (!video) return;
        video.pause();
        if (rewind) video.currentTime = 0;
        this.syncToggle(video, false);
      }

      syncToggle(video, playing) {
        const toggle = video.parentElement && video.parentElement.querySelector('[data-video-toggle]');
        if (!toggle) return;
        toggle.setAttribute('aria-pressed', playing ? 'false' : 'true');
        toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      }

      handleToggle(event) {
        const toggle = event.target.closest('[data-video-toggle]');
        if (!toggle) return;
        const video = toggle.parentElement.querySelector('video');
        if (!video) return;
        if (video.paused) {
          this.userPaused.set(video, false);
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
          this.syncToggle(video, true);
        } else {
          this.userPaused.set(video, true);
          this.pauseVideo(video, false);
        }
      }

      updateVideos() {
        // Clones (loop mode) are not in this.elements.slides, so query live.
        this.querySelectorAll('.splide__slide').forEach((slide) => {
          const video = slide.querySelector('video');
          if (!video) return;
          const slideNum = parseInt(slide.dataset.slide);
          const isActive = slide.classList.contains('is-active') && !slide.classList.contains('splide__slide--clone');
          if (isActive && this.slider.index === slideNum) {
            this.playVideo(video);
          } else {
            this.pauseVideo(video, true);
          }
        });
      }

      // --- lifecycle -----------------------------------------------------------

      connectedCallback() {
        this.addEventListener('click', this.handleToggle.bind(this));

        setTimeout(() => {
          this.slider = new Splide(this.elements.slideshow, {
            type: 'loop',
            pagination: false,
            speed: 600,
            // Only fetch images for the current slide and its neighbours.
            lazyLoad: 'nearby',
            preloadPages: 1,
          }).mount();
          this.slider.go(0);

          // Initial state: play the first slide's video (if any), make sure the rest stay idle.
          this.updateVideos();

          if (this.elements.thumbnails) {
            this.elements.thumbnails.forEach((thumb) => {
              thumb.addEventListener('click', () => {
                const slide = parseInt(thumb.dataset.slide);
                this.slider.go(slide);
              });
            });
          }

          this.slider.on('move', () => {
            if (this.elements.thumbnails) {
              this.elements.thumbnails.forEach((thumb) => {
                const slide = parseInt(thumb.dataset.slide);
                if (this.slider.index === slide) {
                  thumb.setAttribute('aria-selected', 'true');
                  thumb.disabled = true;
                } else {
                  thumb.setAttribute('aria-selected', 'false');
                  thumb.disabled = false;
                }
              });
            }

            if (this.elements.pagination) {
              this.elements.pagination.forEach((page) => {
                const slide = parseInt(page.dataset.slide);
                if (this.slider.index === slide) {
                  page.classList.add('tw-bg-current');
                } else {
                  page.classList.remove('tw-bg-current');
                }
              });
            }
          });

          // Start/stop videos once the move has settled (is-active classes are final).
          this.slider.on('moved', () => this.updateVideos());

          // Pause everything when the gallery leaves the viewport; resume the active one when it returns.
          if ('IntersectionObserver' in window) {
            new IntersectionObserver((entries) => {
              if (entries.some((e) => e.isIntersecting)) {
                this.updateVideos();
              } else {
                this.querySelectorAll('video').forEach((v) => this.pauseVideo(v, false));
              }
            }).observe(this);
          }
        }, 500);
      }
    }
  );
}
